import { useState, FormEvent, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { submitQuote } from '@/api/client';

const SERVICE_OPTIONS = [
  'Deep Cleaning',
  'Regular Cleaning',
  'Light Cleaning',
  'Airbnb Cabin Cleaning',
];

interface QuoteFormData {
  fullName: string;
  phone: string;
  email: string;
  city: string;
  address: string;
  squareFeet: string;
  serviceType: string;
  bedrooms: string;
  bathrooms: string;
  kitchens: string;
  living_rooms: string;
}

const initialForm: QuoteFormData = {
  fullName: '',
  phone: '',
  email: '',
  city: '',
  address: '',
  squareFeet: '',
  serviceType: '',
  bedrooms: '',
  bathrooms: '',
  kitchens: '',
  living_rooms: '',
};

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuoteModal({ isOpen, onClose }: QuoteModalProps) {
  const { t } = useTranslation('home');
  const [formData, setFormData] = useState<QuoteFormData>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialForm);
      return;
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    // Prevent negative numbers for numeric room fields
    const numericFields = ['bedrooms', 'bathrooms', 'kitchens', 'living_rooms'];
    if (numericFields.includes(name)) {
      const num = parseInt(value, 10);
      if (value !== '' && (isNaN(num) || num < 0)) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (
      !formData.fullName ||
      !formData.phone ||
      !formData.email ||
      !formData.city ||
      !formData.serviceType
    ) {
      Swal.fire({
        title: t('quote_required'),
        text: t('quote_required_msg'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const combinedAddress = [formData.city, formData.address]
        .filter(Boolean)
        .join(' — ');

      const result = await submitQuote({
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        property_address: combinedAddress || undefined,
        property_type: 'residential',
        square_feet: formData.squareFeet || undefined,
        cleaning_type: formData.serviceType,
        bedrooms: formData.bedrooms || undefined,
        bathrooms: formData.bathrooms || undefined,
        kitchens: formData.kitchens || undefined,
        living_rooms: formData.living_rooms || undefined,
      });

      if (result.success) {
        Swal.fire({
          title: t('quote_success'),
          text: t('quote_success_body'),
          icon: 'success',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: t('form_thanks'),
        });
        setFormData(initialForm);
        handleClose();
      } else {
        Swal.fire({
          title: t('quote_error'),
          text: result.message || t('quote_error_msg'),
          icon: 'error',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: t('admin_ok'),
        });
      }
    } catch {
      Swal.fire({
        title: t('quote_error'),
        text: t('quote_error_msg'),
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors bg-white';

  const selectClass =
    'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark bg-white focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors appearance-none';

  const labelClass = 'block text-sm font-medium text-brand-dark mb-1.5';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('quote_modal_title')}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl my-8 flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white rounded-t-2xl shrink-0 z-10">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-bold text-brand-dark font-heading truncate">
              {t('quote_modal_title')}
            </h2>
            <p className="text-xs md:text-sm text-brand-muted mt-0.5">
              Get a fast estimate — fill in just a few details.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-brand-light transition-colors cursor-pointer shrink-0 ml-4"
            aria-label={t('quote_close')}
          >
            <i className="ri-close-line text-xl text-brand-dark" />
          </button>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 overflow-y-auto">
          {/* Row 1: Full Name + Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote_fullName" className={labelClass}>
                {t('quote_fullName')} <span className="text-red-500">*</span>
              </label>
              <input
                id="quote_fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                placeholder={t('quote_fullName_placeholder')}
                className={inputClass}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="quote_phone" className={labelClass}>
                {t('quote_phone')} <span className="text-red-500">*</span>
              </label>
              <input
                id="quote_phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('quote_phone_placeholder')}
                className={inputClass}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Row 2: Email */}
          <div>
            <label htmlFor="quote_email" className={labelClass}>
              {t('quote_email')} <span className="text-red-500">*</span>
            </label>
            <input
              id="quote_email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('quote_email_placeholder')}
              className={inputClass}
              disabled={isSubmitting}
            />
          </div>

          {/* Row 3: City + Square Feet */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote_city" className={labelClass}>
                {t('quote_city')} <span className="text-red-500">*</span>
              </label>
              <input
                id="quote_city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                placeholder={t('quote_city_placeholder')}
                className={inputClass}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="quote_squareFeet" className={labelClass}>
                {t('quote_squareFeet')}
              </label>
              <input
                id="quote_squareFeet"
                name="squareFeet"
                type="text"
                value={formData.squareFeet}
                onChange={handleChange}
                placeholder={t('quote_squareFeet_placeholder')}
                className={inputClass}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Row 4: Property Address */}
          <div>
            <label htmlFor="quote_address" className={labelClass}>
              {t('quote_address')}
            </label>
            <input
              id="quote_address"
              name="address"
              type="text"
              value={formData.address}
              onChange={handleChange}
              placeholder={t('quote_address_placeholder')}
              className={inputClass}
              disabled={isSubmitting}
            />
          </div>

          {/* Row 5: Service Type */}
          <div>
            <label htmlFor="quote_serviceType" className={labelClass}>
              {t('form_serviceType')} <span className="text-red-500">*</span>
            </label>
            <select
              id="quote_serviceType"
              name="serviceType"
              value={formData.serviceType}
              onChange={handleChange}
              className={selectClass}
              disabled={isSubmitting}
            >
              <option value="">{t('form_serviceType_placeholder')}</option>
              {SERVICE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Row 6: Room counts */}
          <div>
            <p className="text-sm font-medium text-brand-dark mb-2">Room Details <span className="text-xs font-normal text-brand-muted">(optional)</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="quote_bedrooms" className={labelClass}>Bedrooms</label>
                <input
                  id="quote_bedrooms"
                  name="bedrooms"
                  type="number"
                  min={0}
                  value={formData.bedrooms}
                  onChange={handleChange}
                  placeholder="0"
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="quote_bathrooms" className={labelClass}>Bathrooms</label>
                <input
                  id="quote_bathrooms"
                  name="bathrooms"
                  type="number"
                  min={0}
                  value={formData.bathrooms}
                  onChange={handleChange}
                  placeholder="0"
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="quote_kitchens" className={labelClass}>Kitchens</label>
                <input
                  id="quote_kitchens"
                  name="kitchens"
                  type="number"
                  min={0}
                  value={formData.kitchens}
                  onChange={handleChange}
                  placeholder="0"
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="quote_living_rooms" className={labelClass}>Living Rooms</label>
                <input
                  id="quote_living_rooms"
                  name="living_rooms"
                  type="number"
                  min={0}
                  value={formData.living_rooms}
                  onChange={handleChange}
                  placeholder="0"
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Quick contact note */}
          <div className="flex items-center gap-2 p-3 bg-brand-light rounded-lg">
            <i className="ri-phone-line text-brand-mint text-sm shrink-0" />
            <p className="text-xs text-brand-muted">
              For faster estimates call us directly:{' '}
              <a
                href="tel:+17076236359"
                className="font-semibold text-brand-dark hover:text-brand-mint transition-colors"
              >
                (707) 623-6359
              </a>
            </p>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-1 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-brand-dark hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {t('admin_cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  {t('quote_submitting')}
                </>
              ) : (
                <>
                  <i className="ri-file-list-3-line" />
                  {t('quote_submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}